import { RefObject, useEffect } from 'react';
import { clientPortalApi } from './api';

// Prefijo genérico del canal — compartido por todos los análisis del
// portal (antes fijo a `linea500:`). Debe coincidir exactamente con las
// mismas constantes embebidas en `final-visor-v3-template.html`.
const MSG_SAVE = 'analysis:programming:save';
const MSG_SAVE_RESULT = 'analysis:programming:saveResult';
const MSG_HISTORY_REQUEST = 'analysis:programming:historyRequest';
const MSG_HISTORY_RESULT = 'analysis:programming:historyResult';
const MSG_HISTORY_REVISION_REQUEST = 'analysis:programming:historyRevisionRequest';
const MSG_HISTORY_REVISION_RESULT = 'analysis:programming:historyRevisionResult';

interface ProgrammingMessage {
  type: string;
  channelId: string;
  config?: Record<string, unknown>;
  expectedRevision?: number;
  revision?: number;
}

interface UseProgrammingBridgeArgs {
  slug: string;
  analysisId: string;
  token: string | null;
  iframeRef: RefObject<HTMLIFrameElement>;
  channelIdRef: RefObject<string>;
  targetOriginRef: RefObject<string | null>;
}

/**
 * Puente `postMessage` entre el template (que nunca hace fetch
 * autenticado ni conoce el JWT) y React (que posee la sesión). Todo
 * mensaje entrante se descarta salvo que venga del `contentWindow`
 * exacto del iframe vigente Y lleve el `channelId` de esta instancia —
 * cubre el caso de un iframe viejo vivo un instante tras un re-mount.
 */
export function useProgrammingBridge({
  slug,
  analysisId,
  token,
  iframeRef,
  channelIdRef,
  targetOriginRef,
}: UseProgrammingBridgeArgs): void {
  useEffect(() => {
    if (!token) return undefined;
    const authToken = token;

    function postToIframe(message: Record<string, unknown>): void {
      const win = iframeRef.current?.contentWindow;
      if (!win) return;
      win.postMessage(message, targetOriginRef.current ?? '*');
    }

    function isTrustedSource(event: MessageEvent): boolean {
      if (event.source !== iframeRef.current?.contentWindow) return false;
      if (targetOriginRef.current && event.origin !== targetOriginRef.current) return false;
      return true;
    }

    function handleSave(msg: ProgrammingMessage): void {
      if (!msg.config || typeof msg.expectedRevision !== 'number') return;
      clientPortalApi
        .saveProgramming(slug, analysisId, authToken, {
          config: msg.config,
          expectedRevision: msg.expectedRevision,
        })
        .then(result => {
          postToIframe({ type: MSG_SAVE_RESULT, channelId: msg.channelId, ...result });
        })
        .catch(() => {
          postToIframe({
            type: MSG_SAVE_RESULT,
            channelId: msg.channelId,
            ok: false,
            conflict: false,
            message: 'No se pudo guardar la configuración.',
          });
        });
    }

    function handleHistoryRequest(msg: ProgrammingMessage): void {
      clientPortalApi
        .getProgrammingHistory(slug, analysisId, authToken)
        .then(items => {
          postToIframe({ type: MSG_HISTORY_RESULT, channelId: msg.channelId, ok: true, items });
        })
        .catch(() => {
          postToIframe({
            type: MSG_HISTORY_RESULT,
            channelId: msg.channelId,
            ok: false,
            message: 'No se pudo obtener el historial.',
          });
        });
    }

    function handleHistoryRevisionRequest(msg: ProgrammingMessage): void {
      if (typeof msg.revision !== 'number') return;
      clientPortalApi
        .getProgrammingHistoryRevision(slug, analysisId, authToken, msg.revision)
        .then(detail => {
          postToIframe({
            type: MSG_HISTORY_REVISION_RESULT,
            channelId: msg.channelId,
            ok: true,
            detail,
          });
        })
        .catch(() => {
          postToIframe({
            type: MSG_HISTORY_REVISION_RESULT,
            channelId: msg.channelId,
            ok: false,
            message: 'No se pudo obtener esa revisión.',
          });
        });
    }

    function onMessage(event: MessageEvent<ProgrammingMessage>): void {
      if (!isTrustedSource(event)) return;
      const msg = event.data;
      if (!msg || msg.channelId !== channelIdRef.current) return;

      if (msg.type === MSG_SAVE) handleSave(msg);
      else if (msg.type === MSG_HISTORY_REQUEST) handleHistoryRequest(msg);
      else if (msg.type === MSG_HISTORY_REVISION_REQUEST) handleHistoryRevisionRequest(msg);
    }

    window.addEventListener('message', onMessage);
    return (): void => {
      window.removeEventListener('message', onMessage);
    };
  }, [slug, analysisId, token, iframeRef, channelIdRef, targetOriginRef]);
}
